import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { WHATSAPP_HREF } from "@/lib/links";
import { trackWhatsAppClick } from "@/lib/analytics";

const ease = [0.22, 1, 0.36, 1];

/**
 * Reusable CTA banner used at the bottom of every deep page (Services, Work, About).
 * Contact page uses its own inline form so this component isn't needed there.
 */
export default function ContactCTA({
    kicker = "Let's talk",
    title = "Let's plant something.",
    body = "Tell us where you are — we'll tell you what's next.",
    "data-testid": testId = "contact-cta-banner",
}) {
    return (
        <section
            data-testid={testId}
            className="bg-obsidian text-bone relative py-24 lg:py-32 grain overflow-hidden"
        >
            <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative z-10">
                <div className="grid grid-cols-12 gap-y-10 lg:gap-x-10 items-end">
                    <div className="col-span-12 lg:col-span-8">
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-bone/50 mb-6">
                            / {kicker}
                        </div>
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease }}
                            className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-6xl lg:text-[7.5vw]"
                        >
                            {title}
                        </motion.h2>
                        <p className="mt-8 font-serif italic text-2xl lg:text-3xl text-bone/70 max-w-2xl leading-snug">
                            {body}
                        </p>
                    </div>

                    <div className="col-span-12 lg:col-span-4 flex flex-col gap-3">
                        <a
                            data-testid="contact-cta-banner-whatsapp"
                            href={WHATSAPP_HREF}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                                trackWhatsAppClick({
                                    section: "contact_cta_banner",
                                    buttonName: "cta_banner_whatsapp",
                                })
                            }
                            className="group inline-flex items-center justify-between gap-3 bg-olive text-obsidian px-6 py-5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-bone transition-colors duration-300"
                        >
                            <span className="flex items-center gap-3">
                                <span
                                    aria-hidden
                                    className="inline-block w-2 h-2 bg-obsidian rounded-full"
                                />
                                Chat on WhatsApp
                            </span>
                            <span
                                aria-hidden
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            >
                                →
                            </span>
                        </a>
                        <Link
                            to="/contact"
                            data-testid="contact-cta-banner-form"
                            className="group inline-flex items-center justify-between gap-3 border border-bone/30 text-bone px-6 py-5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-bone hover:text-obsidian transition-colors duration-300"
                        >
                            Send a written brief
                            <span
                                aria-hidden
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            >
                                →
                            </span>
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}
